import { useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteScreen() {
  const videoRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const reveal = () => setRevealed(true);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (v && v.duration && v.currentTime >= v.duration - 2.4) reveal();
  };

  const skip = () => {
    const v = videoRef.current;
    if (v && v.duration) {
      try {
        v.currentTime = v.duration - 0.1;
      } catch (_) {}
    }
    reveal();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email });
      setState("done");
      toast.success(
        data.duplicate
          ? "You're already on the list — your invite is safe."
          : "You're on the list. Your invite is on its way."
      );
    } catch (err) {
      setState("idle");
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail[0]?.msg
        : detail || "Something went wrong. Please try again.";
      toast.error(msg);
    }
  };

  return (
    <section
      data-testid="invite-screen"
      className="relative h-[100dvh] w-full overflow-hidden"
      style={{ background: "linear-gradient(180deg, #155A95 0%, #6AA6CF 100%)" }}
    >
      <video
        ref={videoRef}
        data-testid="intro-video"
        className="absolute inset-0 h-full w-full object-contain md:object-cover"
        autoPlay
        muted
        playsInline
        onTimeUpdate={handleTimeUpdate}
        onEnded={reveal}
        onError={reveal}
      >
        <source src="/juno-intro-mobile.webm" type="video/webm" media="(max-width: 768px)" />
        <source src="/juno-intro-mobile.mp4" type="video/mp4" media="(max-width: 768px)" />
        <source src="/juno-intro.webm" type="video/webm" />
        <source src="/juno-intro.mp4" type="video/mp4" />
      </video>

      {!revealed && (
        <motion.button
          data-testid="skip-intro-button"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          onClick={skip}
          className="absolute bottom-6 right-6 z-20 border border-[#F9F8F5]/40 px-6 py-3 text-[11px] uppercase tracking-[0.25em] text-[#F9F8F5] transition-colors hover:bg-[#F9F8F5] hover:text-[#0E0F0C] md:bottom-8 md:right-10"
        >
          Skip intro
        </motion.button>
      )}

      <AnimatePresence>
        {revealed && (
          <motion.div
            data-testid="invite-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/40 px-5 text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: -14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
            >
              <img
                data-testid="brand-wordmark"
                src="/juno-logo.png"
                alt="Juno — Decoration Kit"
                className="w-32 md:w-40"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 w-full max-w-[480px] px-2 md:mt-10"
            >
              <img
                data-testid="invite-headline"
                src="/juno-headline.png"
                alt="The Party is about to Start"
                className="h-auto w-full"
              />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1 }}
              className="mt-3 text-lg font-light tracking-wide md:text-xl"
            >
              Get your Invite here
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="mt-8 w-full max-w-xl"
            >
              {state === "done" ? (
                <div
                  data-testid="waitlist-success"
                  className="rounded-3xl border border-white/60 px-6 py-5 sm:rounded-full sm:px-8"
                >
                  <p className="font-script flex items-center justify-center gap-2 text-3xl">
                    <Check className="h-5 w-5" />
                    You're in — your invite is coming
                  </p>
                </div>
              ) : (
                <form
                  data-testid="waitlist-form"
                  onSubmit={submit}
                  className="flex flex-col gap-3 rounded-3xl border border-white/70 p-2 transition-colors focus-within:border-white sm:flex-row sm:items-center sm:gap-2 sm:rounded-full sm:p-1.5"
                >
                  <input
                    data-testid="waitlist-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your Email ID"
                    className="w-full bg-transparent px-5 py-2.5 text-center text-base text-white outline-none placeholder:text-white/50 sm:text-left md:text-base"
                  />
                  <button
                    data-testid="waitlist-submit-button"
                    type="submit"
                    disabled={state === "loading"}
                    className="w-full shrink-0 rounded-full bg-white px-6 py-3 text-xs font-medium text-black transition-colors hover:bg-[#8C3C2A] hover:text-white disabled:opacity-50 sm:w-auto md:px-8"
                  >
                    {state === "loading" ? "Joining…" : "Get your Invite"}
                  </button>
                </form>
              )}
            </motion.div>

            <motion.p
              data-testid="invite-subline"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.45 }}
              className="mt-6 w-full max-w-md px-2 text-xs leading-relaxed text-white/80 md:text-sm"
            >
              Join the waitlist and get 10% additional discount, free
              shipping and a surprise freebie on your first Curated Decoration
              Kit
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
